import type { Language } from '@seek/ca-myprofile-utils';
import { createForm } from '@seek/forms-ui';
import { useLanguage } from '@vocab/react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useApiClient } from 'src/modules/ApiProvider';
import type {
  ChangeEmailVerifyFailure,
  ChangeEmailStartFailure,
} from 'src/modules/graphql/types';
import { PageUpdateActionType } from 'src/reducers/page/actionCreators';
import {
  UserActionType,
  type ChangeEmailVerifyModel,
} from 'src/reducers/user/actionCreators';
import type { ServerError as ServerErrorType } from 'src/types';

import Form, { FORM_NAME } from './Form';

const { FormProvider, useField } = createForm<ChangeEmailVerifyModel>(
  FORM_NAME,
  {
    formType: 'braid',
  },
);
export type EmailUseField = typeof useField;

const VerifyCodeForm = ({
  toggle,
  newEmail,
  setSuccess,
}: {
  toggle: () => void;
  newEmail: string;
  setSuccess: (state: boolean) => void;
}) => {
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const api = useApiClient();

  const [submitting, setSubmitting] = React.useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<ServerErrorType | null>(null);
  const { trackAccountLink } = useAnalytics();

  const onSubmit = () => {
    setSubmitting(true);

    api
      .changeEmailVerify({ newEmail, verificationCode: code })
      .then((result: any) => {
        if (result?.__typename === 'ChangeEmailVerifySuccess') {
          // Update to new email
          dispatch({
            type: UserActionType.UPDATE_USER_STATE,
            payload: {
              emailAddress: newEmail,
            },
          });

          // Sends analytics
          dispatch({
            type: PageUpdateActionType.SET_CHANGE_EMAIL_SUCCESS,
          });

          toggle();
          setSuccess(true);
        } else if (result?.__typename === 'ChangeEmailVerifyFailure') {
          const failure = result as ChangeEmailVerifyFailure;

          let errorCode, errorMessage;

          if (failure.error.__typename === 'AccountValidationError') {
            errorCode = failure.error?.code ?? failure.error?.details[0]?.code;
            errorMessage =
              failure.error?.message ?? failure.error?.details[0]?.message;
          } else {
            errorMessage = failure.error.message;
          }
          setError({
            ...(errorCode && { errorCode }),
            ...(errorMessage && { errorMessage }),
          });
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setError({ errorMessage: err?.message || 'Oops!' });
        setSubmitting(false);
      });

    return;
  };

  const onSubmitResend = () => {
    setSubmitting(true);

    trackAccountLink({
      eventName: 'resend_code_pressed',
      currentPageSection: 'code verification',
      identityProvider: 'auth0',
      authenticationFlow: 'changeEmail',
      linkText: 'resend the code',
      linkPosition: 'body',
      isThirdPartyLink: 'false',
    });

    api
      .changeEmailStart({ newEmail })
      .then((result: any) => {
        if (result?.__typename === 'ChangeEmailStartFailure') {
          const failure = result as ChangeEmailStartFailure;

          let errorMessage;
          if (failure.error.__typename === 'AccountValidationError') {
            errorMessage =
              failure.error?.message ?? failure.error?.details[0]?.message;
          } else {
            errorMessage = failure.error.message;
          }
          setError({ errorMessage });
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setError({ errorMessage: err?.message || 'Oops!' });
        setSubmitting(false);
      });

    return;
  };

  return (
    <FormProvider
      initialValues={{
        newEmail: '',
      }}
      language={language as Language}
    >
      {({ handleSubmit }) => (
        <Form
          onSubmit={handleSubmit(onSubmit)}
          onSubmitResend={onSubmitResend}
          submitting={submitting}
          toggle={toggle}
          code={code}
          error={error}
          newEmail={newEmail}
          setCode={setCode}
          setError={setError}
        />
      )}
    </FormProvider>
  );
};

export default VerifyCodeForm;
